import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Eye, EyeOff, GripVertical, Lock, Pencil, Sparkles, Trash2, Wrench, Zap } from 'lucide-react';
import { describeCondition, fieldTypeLabel, hasOtherOption, needsCustomField, type FormField } from '../utils/requestTypeFields';

interface RequestTypeFieldCardProps {
  field: FormField;
  index: number;
  /** Every field on the form, so a condition can name the field it depends on. */
  allFields: FormField[];
  /** A locked request type shows the card without drag, edit or delete. */
  locked?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onMove?: (dragIndex: number, hoverIndex: number) => void;
}

/**
 * One field on the request form (RESTORE_PLAN R1.2): the row the old DraggableFieldItem and
 * FieldOrderingInterface drew, with everything the field holds in view: default value, the
 * show/hide condition, the full option list (and the "Other" note), cascading parents and
 * children, automation triggers; and the badges: type, Required, System, AI, Visible or Internal,
 * custom field, consultant review.
 */
export function RequestTypeFieldCard(props: RequestTypeFieldCardProps) {
  if (props.locked) return <FieldCardBody {...props} />;
  return <DraggableFieldCard {...props} />;
}

function DraggableFieldCard(props: RequestTypeFieldCardProps) {
  const { field, index, onMove } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop({
    accept: 'field',
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() };
    },
    hover(item: any, monitor) {
      if (!ref.current || !onMove) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      // Only move once the pointer has crossed the middle of the card, so rows do not flicker.
      const rect = ref.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;
      const clientY = (monitor.getClientOffset()?.y ?? 0) - rect.top;
      if (dragIndex < hoverIndex && clientY < middleY) return;
      if (dragIndex > hoverIndex && clientY > middleY) return;
      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'field',
    item: () => ({ id: field.id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  drag(drop(ref));
  return <FieldCardBody {...props} cardRef={ref} handlerId={handlerId} isDragging={isDragging} />;
}

function FieldCardBody({ field, index, allFields, locked, onEdit, onDelete, cardRef, handlerId, isDragging }: RequestTypeFieldCardProps & { cardRef?: React.RefObject<HTMLDivElement>; handlerId?: unknown; isDragging?: boolean }) {
  const options = field.options || [];
  const cascading = field.cascadingOptions || [];
  const triggers = field.automationTriggers || [];
  const condition = describeCondition(field, allFields);
  const customField = needsCustomField(field);
  const hidden = !!field.hideAndPrepopulate;
  const hasDetails = !!field.defaultValue || !!condition || (field.type !== 'cascading' && options.length > 0) || cascading.length > 0 || triggers.length > 0 || hidden;

  return (
    <div
      ref={cardRef}
      data-handler-id={handlerId as string | undefined}
      data-testid="request-type-field-card"
      className={`bg-white border rounded-lg ${isDragging ? 'opacity-50' : ''} ${locked ? 'bg-gray-50 opacity-80' : ''}`}
    >
      <div className="flex items-start gap-3 p-3">
        {locked ? (
          <Lock className="w-4 h-4 text-gray-400 mt-1" aria-label="Locked" />
        ) : (
          <GripVertical className="w-4 h-4 text-gray-400 cursor-grab mt-1" aria-label="Drag to reorder" />
        )}
        <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center justify-center shrink-0 mt-0.5" aria-label={`Position ${index + 1}`}>{index + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{field.name || 'Untitled field'}</span>
            {field.required && <Badge variant="destructive" className="text-[10px] h-5 px-1.5">Required</Badge>}
            {field.isSystem && <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] h-5 px-1.5">System</Badge>}
            {field.aiGenerated && (
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI</Badge>
            )}
          </div>
          {field.portalLabel && <p className="text-xs text-muted-foreground">Shown on the portal as "{field.portalLabel}"</p>}
          {field.helpText && <p className="text-xs text-muted-foreground mt-0.5">{field.helpText}</p>}
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mt-1.5">
            <Badge variant="outline" className="text-[10px] h-5">{fieldTypeLabel(field.type)}</Badge>
            <span className="flex items-center gap-1" data-testid="field-visibility">
              {hidden ? <><EyeOff className="h-3 w-3" /> Internal</> : <><Eye className="h-3 w-3" /> Visible</>}
            </span>
            {field.type !== 'cascading' && options.length > 0 && <span>{options.length} {field.type === 'asset' ? 'asset type' : 'option'}{options.length === 1 ? '' : 's'}</span>}
            {cascading.length > 0 && <span>{cascading.length} parent option{cascading.length === 1 ? '' : 's'}</span>}
            {customField && <span className="flex items-center gap-1 text-slate-700"><Wrench className="h-3 w-3" /> Custom field</span>}
            {field.flaggedForConsultant && <span className="text-amber-700">Consultant review</span>}
            {field.usedForReporting && <span>Reporting</span>}
            {field.usedForAutomation && <span>Automation</span>}
          </div>
        </div>
        {!locked && (
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} aria-label={`Edit ${field.name || 'field'}`}><Pencil className="w-4 h-4" /></Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={onDelete}
              disabled={field.isSystem}
              aria-label={`Delete ${field.name || 'field'}`}
            >
              {field.isSystem ? <Lock className="w-4 h-4 text-gray-400" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          </div>
        )}
      </div>

      {hasDetails && (
        <div className="border-t px-3 py-2 pl-[4.25rem] space-y-2 text-xs" data-testid="field-card-details">
          {field.defaultValue && (
            <p className="text-muted-foreground">Default: <Badge variant="secondary" className="text-[10px] h-5">{field.defaultValue}</Badge></p>
          )}
          {hidden && (
            <p className="text-muted-foreground">Hidden from the portal, filled with <Badge variant="secondary" className="text-[10px] h-5">{field.prepopulatedValue || ''}</Badge></p>
          )}
          {condition && <p className="text-muted-foreground italic" data-testid="field-card-condition">{condition}</p>}
          {field.type !== 'cascading' && options.length > 0 && (
            <div>
              <div className="flex flex-wrap gap-1">
                {options.map((o, i) => <Badge key={`${o}-${i}`} variant="secondary" className="text-[10px] h-5">{o}</Badge>)}
              </div>
              {hasOtherOption(options) && <p className="text-muted-foreground mt-1">A short text field appears when "Other" is chosen.</p>}
            </div>
          )}
          {cascading.length > 0 && (
            <div className="space-y-1" data-testid="field-card-cascading">
              {cascading.map((c, i) => (
                <div key={`${c.parentValue}-${i}`} className="flex flex-wrap items-center gap-1">
                  <Badge className="text-[10px] h-5">{c.parentValue}</Badge>
                  <span className="text-muted-foreground">→</span>
                  {c.childValues.map((v, j) => <Badge key={`${v}-${j}`} variant="outline" className="text-[10px] h-5">{v}</Badge>)}
                </div>
              ))}
            </div>
          )}
          {triggers.length > 0 && (
            <div className="space-y-1" data-testid="field-card-triggers">
              {triggers.map((t) => (
                <p key={t.id} className="flex items-start gap-1 text-muted-foreground">
                  <Zap className="h-3 w-3 text-amber-600 mt-0.5 shrink-0" />
                  <span>When <Badge className="text-[10px] h-5">{t.triggerValue}</Badge>{t.automationDescription ? `: ${t.automationDescription}` : ''}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
